import { useEffect, useRef, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  CircleHelp,
  Image,
  MapPin,
  Phone,
  User,
  Users,
  X,
} from "lucide-react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Callout,
  Dialog,
  Flex,
  Grid,
  Heading,
  Separator,
  Spinner,
  Text,
  TextArea,
  TextField,
} from "@radix-ui/themes";
import { Link } from "@inertiajs/react";
import { SubmitEvent } from "react";
import { TeamFormData } from "@/types/shared";

// ── Types ─────────────────────────────────────────────────────────────────────

interface RosterMember {
  email: string;
  isOwner?: boolean;
  onPlatform?: boolean;
}

interface LookupResult {
  found: boolean;
  email: string;
}

interface TeamFormProps {
  data: TeamFormData;
  setData: <K extends keyof TeamFormData>(key: K, value: TeamFormData[K]) => void;
  onSubmit: (e: SubmitEvent) => void;
  processing: boolean;
  errors?: string[];
  currentUserEmail: string;
  title: string;
  submitLabel: string;
  initialRoster?: RosterMember[];
}

// ── Step indicator ─────────────────────────────────────────────────────────────

function StepIndicator({ step, currentStep }: { step: number; currentStep: number }) {
  const done = currentStep > step;
  const active = currentStep === step;

  return (
    <Flex align="center" justify="center"
      style={{
        width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
        background: done ? "var(--green-9)" : active ? "var(--accent-9)" : "var(--gray-4)",
        color: done || active ? "white" : "var(--gray-10)",
        fontSize: 12, fontWeight: 700,
      }}
    >
      {done ? <CheckCircle2 size={14} /> : step}
    </Flex>
  );
}

function Stepper({ currentStep, onStepClick }: { currentStep: number; onStepClick: (n: number) => void }) {
  const steps = ["Team Details", "Roster"];
  return (
    <Flex align="center" gap="2" mb="5">
      {steps.map((label, i) => {
        const n = i + 1;
        const clickable = n < currentStep;
        return (
          <Flex key={n} align="center" gap="2" style={{ flex: n < steps.length ? 1 : "none" }}>
            <Flex
              align="center" gap="2"
              onClick={() => clickable && onStepClick(n)}
              style={{ cursor: clickable ? "pointer" : "default", userSelect: "none" }}
            >
              <StepIndicator step={n} currentStep={currentStep} />
              <Text
                size="2"
                weight={currentStep === n ? "medium" : "regular"}
                color={currentStep === n ? undefined : "gray"}
              >
                {label}
              </Text>
            </Flex>
            {n < steps.length && (
              <Box style={{ flex: 1, height: 1, background: currentStep > n ? "var(--green-9)" : "var(--gray-5)", margin: "0 4px" }} />
            )}
          </Flex>
        );
      })}
    </Flex>
  );
}

// ── Add Member Modal ───────────────────────────────────────────────────────────

function AddMemberModal({
  onAdd,
  existingEmails,
}: {
  onAdd: (email: string, onPlatform: boolean) => void;
  existingEmails: string[];
}) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [lookupResult, setLookupResult] = useState<LookupResult | null>(null);
  const [searching, setSearching] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const alreadyAdded = lookupResult ? existingEmails.includes(lookupResult.email.toLowerCase()) : false;

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setLookupResult(null);

    const trimmed = email.trim();
    if (!trimmed.includes("@") || !trimmed.includes(".")) {
      setSearching(false);
      return;
    }

    timerRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(`/users/lookup?email=${encodeURIComponent(trimmed)}`);
        const data: LookupResult = await res.json();
        setLookupResult(data);
      } finally {
        setSearching(false);
      }
    }, 500);

    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [email]);

  function handleAdd(onPlatform: boolean) {
    if (!lookupResult) return;
    onAdd(lookupResult.email, onPlatform);
    setOpen(false);
    setEmail("");
    setLookupResult(null);
  }

  function handleOpenChange(v: boolean) {
    setOpen(v);
    if (!v) { setEmail(""); setLookupResult(null); }
  }

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Trigger>
        <Button variant="outline" color="gray" style={{ width: "100%", justifyContent: "center" }}>
          <Users size={15} /> Add team member
        </Button>
      </Dialog.Trigger>

      <Dialog.Content maxWidth="420px">
        <Dialog.Title>Add a member</Dialog.Title>
        <Dialog.Description size="2" color="gray" mb="4">
          Enter their email to check if they're already on Driftables.
        </Dialog.Description>

        <Flex direction="column" gap="3">
          <TextField.Root
            size="2"
            type="email"
            placeholder="harry@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
          >
            {searching && (
              <TextField.Slot side="right">
                <Spinner size="1" />
              </TextField.Slot>
            )}
          </TextField.Root>

          {/* Found on platform */}
          {lookupResult?.found && !searching && (
            <Box p="3" style={{ background: "var(--green-2)", border: "1px solid var(--green-6)", borderRadius: "var(--radius-3)" }}>
              <Flex gap="3" align="center" mb="3">
                <Avatar size="2" fallback={lookupResult.email[0].toUpperCase()} color="green" radius="full" />
                <Box>
                  <Text size="2" weight="medium">{lookupResult.email}</Text>
                  <Flex gap="1" align="center" mt="1">
                    <CheckCircle2 size={11} color="var(--green-11)" />
                    <Text size="1" color="green">Already on Driftables</Text>
                  </Flex>
                </Box>
              </Flex>
              {alreadyAdded ? (
                <Text size="2" color="gray">This person is already on your roster.</Text>
              ) : (
                <Button size="2" color="green" style={{ width: "100%" }} onClick={() => handleAdd(true)}>
                  Invite to team
                </Button>
              )}
            </Box>
          )}

          {/* Not on platform */}
          {lookupResult && !lookupResult.found && !searching && (
            <Box p="3" style={{ background: "var(--gray-2)", border: "1px solid var(--gray-6)", borderRadius: "var(--radius-3)" }}>
              <Flex gap="3" align="center" mb="3">
                <Avatar size="2" fallback="?" color="gray" radius="full" />
                <Box>
                  <Text size="2" weight="medium">{lookupResult.email}</Text>
                  <Flex gap="1" align="center" mt="1">
                    <CircleHelp size={11} color="var(--gray-10)" />
                    <Text size="1" color="gray">Not on Driftables yet</Text>
                  </Flex>
                </Box>
              </Flex>
              <Text size="2" color="gray" as="p" mb="3">
                That email isn't registered. You can still add them — we'll send a sign-up invite.
              </Text>
              {alreadyAdded ? (
                <Text size="2" color="gray">This person is already on your roster.</Text>
              ) : (
                <Button size="2" variant="soft" color="gray" style={{ width: "100%" }} onClick={() => handleAdd(false)}>
                  Send invite
                </Button>
              )}
            </Box>
          )}
        </Flex>

        <Flex gap="2" justify="end" mt="4">
          <Dialog.Close>
            <Button variant="soft" color="gray">Cancel</Button>
          </Dialog.Close>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}

// ── Roster list ────────────────────────────────────────────────────────────────

function RosterList({
  members,
  onRemove,
}: {
  members: RosterMember[];
  onRemove: (email: string) => void;
}) {
  if (members.length === 0) return null;

  return (
    <Flex direction="column" gap="1">
      {members.map((member) => (
        <Flex key={member.email} align="center" gap="3" py="2"
          style={{ borderBottom: "1px solid var(--gray-4)" }}
        >
          <Avatar
            size="2"
            fallback={member.email[0].toUpperCase()}
            color={member.isOwner ? undefined : "gray"}
            radius="full"
          />
          <Box style={{ flex: 1 }}>
            <Text size="2" weight="medium">{member.email}</Text>
          </Box>
          {member.isOwner ? (
            <Badge variant="soft" radius="full">Owner</Badge>
          ) : member.onPlatform ? (
            <Badge color="green" variant="soft" radius="full">On platform</Badge>
          ) : (
            <Badge color="amber" variant="soft" radius="full">Invited</Badge>
          )}
          {!member.isOwner && (
            <Button size="1" variant="ghost" color="gray" onClick={() => onRemove(member.email)}>
              <X size={13} />
            </Button>
          )}
        </Flex>
      ))}
    </Flex>
  );
}

// ── Main form ──────────────────────────────────────────────────────────────────

export default function TeamForm({
  data,
  setData,
  onSubmit,
  processing,
  errors,
  currentUserEmail,
  title,
  submitLabel,
  initialRoster,
}: TeamFormProps) {
  const [step, setStep] = useState(1);
  const [roster, setRoster] = useState<RosterMember[]>(
    initialRoster ?? [{ email: currentUserEmail, isOwner: true }]
  );

  const setParticipant = (fields: Partial<TeamFormData["participant"]>) =>
    setData("participant", { ...data.participant, ...fields });

  const setTeam = (fields: Partial<Omit<TeamFormData["team"], "photos">>) =>
    setData("team", { ...data.team, ...fields });

  function addMember(email: string, onPlatform: boolean) {
    const normalised = email.toLowerCase();
    if (roster.some((m) => m.email.toLowerCase() === normalised)) return;
    const next = [...roster, { email: normalised, onPlatform }];
    setRoster(next);
    setData(
      "invited_emails",
      next.filter((m) => !m.isOwner).map((m) => m.email),
    );
  }

  function removeMember(email: string) {
    const next = roster.filter((m) => m.email !== email);
    setRoster(next);
    setData(
      "invited_emails",
      next.filter((m) => !m.isOwner).map((m) => m.email),
    );
  }

  const step1Valid = data.participant.name.trim().length > 0 && data.team.description.trim().length > 0;

  return (
    <Box>
      <Heading size="6" mb="1">{title}</Heading>
      <Text size="2" color="gray" as="p" mb="5">
        Teams let you register for events together and share a roster.
      </Text>

      <Stepper currentStep={step} onStepClick={setStep} />

      {errors && errors.length > 0 && (
        <Callout.Root color="red" role="alert" mb="4">
          <Callout.Icon><AlertCircle /></Callout.Icon>
          <Callout.Text>{errors.join(", ")}</Callout.Text>
        </Callout.Root>
      )}

      <form onSubmit={onSubmit}>
        {/* ── Step 1: Details ── */}
        {step === 1 && (
          <Flex direction="column" gap="4">
            {/* Identity */}
            <Box>
              <Text size="2" weight="medium" color="gray" style={{ textTransform: "uppercase", letterSpacing: "0.06em" }} as="p" mb="3">
                Identity
              </Text>
              <Grid columns={{ initial: "1", sm: "2" }} gap="4">
                <Flex direction="column" gap="1">
                  <Text as="label" size="2" weight="medium" htmlFor="team-name">Team name *</Text>
                  <TextField.Root
                    id="team-name"
                    size="2"
                    placeholder="e.g. Apex Motorsport"
                    value={data.participant.name}
                    onChange={(e) => setParticipant({ name: e.target.value })}
                    required
                  >
                    <TextField.Slot><User size={14} /></TextField.Slot>
                  </TextField.Root>
                </Flex>
                <Flex direction="column" gap="1">
                  <Text as="label" size="2" weight="medium" htmlFor="team-location">Location</Text>
                  <TextField.Root
                    id="team-location"
                    size="2"
                    placeholder="e.g. Los Angeles, CA"
                    value={data.participant.location}
                    onChange={(e) => setParticipant({ location: e.target.value })}
                  >
                    <TextField.Slot><MapPin size={14} /></TextField.Slot>
                  </TextField.Root>
                </Flex>
              </Grid>
            </Box>

            <Separator size="4" />

            {/* About */}
            <Box>
              <Text size="2" weight="medium" color="gray" style={{ textTransform: "uppercase", letterSpacing: "0.06em" }} as="p" mb="3">
                About
              </Text>
              <Flex direction="column" gap="1">
                <Text as="label" size="2" weight="medium" htmlFor="team-description">Bio *</Text>
                <Text size="1" color="gray">Shown on your team profile.</Text>
                <TextArea
                  id="team-description"
                  size="2"
                  rows={4}
                  placeholder="Tell people about your team — who you are, what you run, your history…"
                  value={data.team.description}
                  onChange={(e) => setTeam({ description: e.target.value })}
                />
              </Flex>
            </Box>

            <Separator size="4" />

            {/* Contact */}
            <Box>
              <Text size="2" weight="medium" color="gray" style={{ textTransform: "uppercase", letterSpacing: "0.06em" }} as="p" mb="3">
                Contact
              </Text>
              <Grid columns={{ initial: "1", sm: "2" }} gap="4">
                <Flex direction="column" gap="1">
                  <Text as="label" size="2" weight="medium" htmlFor="team-email">Contact email</Text>
                  <TextField.Root
                    id="team-email"
                    size="2"
                    type="email"
                    placeholder="team@example.com"
                    value={data.participant.email}
                    onChange={(e) => setParticipant({ email: e.target.value })}
                  />
                </Flex>
                <Flex direction="column" gap="1">
                  <Text as="label" size="2" weight="medium" htmlFor="team-phone">Phone</Text>
                  <TextField.Root
                    id="team-phone"
                    size="2"
                    placeholder="Optional"
                    value={data.participant.phone_number}
                    onChange={(e) => setParticipant({ phone_number: e.target.value })}
                  >
                    <TextField.Slot><Phone size={14} /></TextField.Slot>
                  </TextField.Root>
                </Flex>
              </Grid>
            </Box>

            <Separator size="4" />

            {/* Logo */}
            <Box>
              <Text size="2" weight="medium" color="gray" style={{ textTransform: "uppercase", letterSpacing: "0.06em" }} as="p" mb="3">
                Logo
              </Text>
              <Flex direction="column" gap="1">
                <Text size="1" color="gray">Square image recommended (PNG or SVG).</Text>
                <Flex align="center" gap="2" mt="1">
                  <Image size={14} color="var(--gray-9)" />
                  <input
                    type="file"
                    accept="image/*"
                    style={{ fontSize: "var(--font-size-2)" }}
                    onChange={(e) => setTeam({ logo: e.target.files?.[0] ?? null })}
                  />
                </Flex>
              </Flex>
            </Box>

            <Flex justify="between" mt="2">
              <Button type="button" variant="soft" color="gray" asChild>
                <Link href="/profile">Cancel</Link>
              </Button>
              <Button
                type="button"
                disabled={!step1Valid}
                onClick={() => setStep(2)}
              >
                Continue to Roster →
              </Button>
            </Flex>
          </Flex>
        )}

        {/* ── Step 2: Roster ── */}
        {step === 2 && (
          <Flex direction="column" gap="4">
            <Box>
              <Flex justify="between" align="baseline" mb="3">
                <Text size="2" weight="medium" color="gray" style={{ textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Members
                </Text>
                <Badge color="gray" variant="soft" radius="full">
                  {roster.length} {roster.length === 1 ? "member" : "members"}
                </Badge>
              </Flex>

              <RosterList members={roster} onRemove={removeMember} />

              <Box mt="3">
                <AddMemberModal
                  onAdd={addMember}
                  existingEmails={roster.map((m) => m.email.toLowerCase())}
                />
              </Box>

              <Callout.Root color="gray" variant="surface" mt="4">
                <Callout.Icon><Users size={16} /></Callout.Icon>
                <Callout.Text>
                  Members on Driftables will be linked directly. Members not yet on the platform will receive a sign-up invite.
                </Callout.Text>
              </Callout.Root>
            </Box>

            <Separator size="4" />

            <Flex justify="between" mt="2">
              <Button type="button" variant="soft" color="gray" onClick={() => setStep(1)}>
                ← Back
              </Button>
              <Button type="submit" disabled={processing}>
                {submitLabel}
              </Button>
            </Flex>
          </Flex>
        )}
      </form>
    </Box>
  );
}
